import {
  signIn,
  signOut,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  updateUserAttributes,
  confirmUserAttribute,
  fetchAuthSession,
  getCurrentUser,
} from 'aws-amplify/auth';

/**
 * Amplify Auth wrapper — thin layer around Amplify SDK.
 * Provides typed functions with Spanish error messages.
 */

export async function login(email: string, password: string) {
  const result = await signIn({ username: email, password });

  if (!result.isSignedIn) {
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
      throw new Error('Cuenta no confirmada. Revise su email.');
    }
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      // First login with temporary password — needs new password
      throw new Error('FORCE_NEW_PASSWORD');
    }
    throw new Error('Error al iniciar sesión');
  }

  return result;
}

export async function logout() {
  await signOut();
}

export async function requestPasswordReset(email: string) {
  const result = await resetPassword({ username: email });
  return result;
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
) {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
}

export async function changePassword(oldPassword: string, newPassword: string) {
  await updatePassword({ oldPassword, newPassword });
}

export async function updateCognitoName(nombre: string) {
  await updateUserAttributes({ userAttributes: { name: nombre } });
}

export async function requestEmailChange(newEmail: string) {
  await updateUserAttributes({ userAttributes: { email: newEmail } });
}

export async function confirmEmailChange(code: string) {
  await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken;
    if (!token) return null;

    // Proactive refresh: if token expires within 5 minutes, force refresh now
    // This prevents sending a token that will expire during the request
    const exp = token.payload.exp;
    if (exp && Date.now() / 1000 > exp - 300) {
      const refreshed = await fetchAuthSession({ forceRefresh: true });
      return refreshed.tokens?.accessToken?.toString() ?? null;
    }

    return token.toString();
  } catch {
    return null;
  }
}

export async function refreshSession(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getAuthenticatedUser() {
  try {
    const user = await getCurrentUser();
    return user;
  } catch {
    return null;
  }
}
